import { useFormikContext } from "formik";
import React from "react";
import { Col, Form, Row } from "react-bootstrap";

import { CountryWithCode, StateWithCode } from "../../../../config/filingConstants";
import { ISelfOccupiedProperties } from "../../../../config/interfaces";
import { AddressFormInitValues } from "../../../../pages/itr-filing/efiling.config";
import { UtilService } from "../../../../services";
import { FormField, NumericInputField, SelectInputField, TextInputField } from "../../../fields";
import PreventEditComponent from "../../../PreventEditComponent";

type Props = { addressValues: any };

const RISelfAddressInfoForm: React.FC<Props> = (props: Props) => {
  const { addressValues } = props;

  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<ISelfOccupiedProperties>();

  return (
    <>
      <section className="mb-5">
        <p className="leading-para fw-bold text-primary">House Address</p>

        <PreventEditComponent>
          <Form.Group className="form-group">
            <Form.Check
              role="button"
              type="checkbox"
              id="IsSameAddress"
              className="align-items-center"
            >
              <Form.Check.Input
                type="checkbox"
                name="IsSameAddress"
                checked={values.IsSameAddress}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFieldValue("Address", addressValues);
                  } else {
                    setFieldValue("Address", AddressFormInitValues);
                  }
                  setFieldValue("IsSameAddress", e.target.checked);
                }}
              />
              <Form.Check.Label>
                Same as the address filled in Personal Information
              </Form.Check.Label>
            </Form.Check>
          </Form.Group>
        </PreventEditComponent>

        <FormField label="Flat/Door/Block No." isRequired={true}>
          <TextInputField
            name="Address.ResidenceNo"
            value={values.Address.ResidenceNo}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue("Address.ResidenceNo", values.Address.ResidenceNo.trim());
            }}
            onChange={(value) => setFieldValue("Address.ResidenceNo", value)}
            isInvalid={Boolean(errors.Address?.ResidenceNo) && touched.Address?.ResidenceNo}
            errorMsg={errors.Address?.ResidenceNo}
          />
        </FormField>

        <FormField label="Building/House Name" isRequired={true}>
          <TextInputField
            name="Address.ResidenceName"
            value={values.Address.ResidenceName}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue("Address.ResidenceName", values.Address.ResidenceName.trim());
            }}
            onChange={(value) => setFieldValue("Address.ResidenceName", value)}
            isInvalid={Boolean(errors.Address?.ResidenceName) && touched.Address?.ResidenceName}
            errorMsg={errors.Address?.ResidenceName}
          />
        </FormField>

        <FormField label="Road/Street" isRequired={true}>
          <TextInputField
            name="Address.RoadOrStreet"
            value={values.Address.RoadOrStreet}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue("Address.RoadOrStreet", values.Address.RoadOrStreet.trim());
            }}
            onChange={(value) => setFieldValue("Address.RoadOrStreet", value)}
            isInvalid={Boolean(errors.Address?.RoadOrStreet) && touched.Address?.RoadOrStreet}
            errorMsg={errors.Address?.RoadOrStreet}
          />
        </FormField>

        <FormField label="Area/Locality" isRequired={true}>
          <TextInputField
            name="Address.LocalityOrArea"
            value={values.Address.LocalityOrArea}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue("Address.LocalityOrArea", values.Address.LocalityOrArea.trim());
            }}
            onChange={(value) => setFieldValue("Address.LocalityOrArea", value)}
            isInvalid={Boolean(errors.Address?.LocalityOrArea) && touched.Address?.LocalityOrArea}
            errorMsg={errors.Address?.LocalityOrArea}
          />
        </FormField>

        <FormField label="PIN Code" isRequired={true}>
          <NumericInputField
            name="Address.PinCode"
            value={values.Address?.PinCode || ""}
            onBlur={handleBlur("Address.PinCode")}
            onChange={async (value) => {
              setFieldValue("Address.PinCode", Number(value));
              setFieldValue("Address.ZipCode", value);

              if (value.length === 6) {
                const data = await UtilService.getStateCityFromPincode(value);
                if (data) {
                  setFieldValue("Address.CountryCode", data.CountryCode);
                  setFieldValue("Address.StateCode", data.StateCode);
                  setFieldValue("Address.CityOrTownOrDistrict", data.CityOrTownOrDistrict);
                } else {
                  setFieldValue("Address.CountryCode", "");
                  setFieldValue("Address.StateCode", "");
                  setFieldValue("Address.CityOrTownOrDistrict", "");
                }
              }
            }}
            isInvalid={Boolean(errors.Address?.PinCode) && touched.Address?.PinCode}
            errorMsg={errors.Address?.PinCode}
            decimalUpto={0}
            separator=""
          />
        </FormField>

        <FormField label="Country | State | City" isRequired={true} sm={3}>
          <Row className="align-items-center">
            <Col sm={4}>
              <SelectInputField
                placeholder="Select Country"
                name="Address.CountryCode"
                label="Country"
                isSearchable={true}
                isOptionArrayOfObject={true}
                options={UtilService.convertToArray(CountryWithCode)}
                value={{
                  "label": CountryWithCode[values.Address.CountryCode],
                  "value": values.Address.CountryCode
                }}
                onChange={(selectedCountry) => {
                  if (selectedCountry) {
                    setFieldValue("Address.CountryCode", selectedCountry.value);
                    setFieldValue("Address.CityOrTownOrDistrict", "");
                    if (selectedCountry.value !== "91") {
                      setFieldValue("Address.StateCode", "99");
                    } else {
                      setFieldValue("Address.StateCode", "");
                    }
                  } else {
                    setFieldValue("Address.CountryCode", undefined);
                  }
                }}
                onBlur={handleBlur("Address.CountryCode")}
                isInvalid={Boolean(errors.Address?.CountryCode) && touched.Address?.CountryCode}
                errorMsg={errors.Address?.CountryCode}
              />
            </Col>
            <Col sm={4}>
              <SelectInputField
                placeholder="Select state"
                name="Address.StateCode"
                label="State"
                isSearchable={true}
                isOptionArrayOfObject={true}
                options={UtilService.getStateOptions(values.Address.CountryCode)}
                value={{
                  "label": StateWithCode[values.Address.StateCode],
                  "value": values.Address.StateCode
                }}
                onChange={(selectedState) => {
                  if (selectedState) {
                    setFieldValue("Address.StateCode", selectedState.value);
                  } else {
                    setFieldValue("Address.StateCode", undefined);
                  }
                }}
                onBlur={handleBlur("Address.StateCode")}
                isInvalid={Boolean(errors.Address?.StateCode) && touched.Address?.StateCode}
                errorMsg={errors.Address?.StateCode}
              />
            </Col>
            <Col sm={4}>
              <TextInputField
                name="Address.CityOrTownOrDistrict"
                label="City"
                value={values.Address.CityOrTownOrDistrict}
                onChange={(value) => {
                  setFieldValue("Address.CityOrTownOrDistrict", value);
                }}
                onBlur={handleBlur("Address.CityOrTownOrDistrict")}
                isInvalid={
                  Boolean(errors.Address?.CityOrTownOrDistrict) &&
                  touched.Address?.CityOrTownOrDistrict
                }
                errorMsg={errors.Address?.CityOrTownOrDistrict}
              />
            </Col>
          </Row>
        </FormField>
      </section>
    </>
  );
};

export default RISelfAddressInfoForm;
